import { Check } from "lucide-react";

export const LOGO_FULL = "/logo-full.png";
export const LOGO_ICON = "/icon-192.png";

export function ProgressBar({ value = 0, className = "", tall = false, testid }) {
  return (
    <div className={`w-full bg-[#EADEC9]/70 rounded-full overflow-hidden ${tall ? "h-3" : "h-2"} ${className}`} data-testid={testid}>
      <div className="h-full bg-[#1DB99A] rounded-full transition-[width] duration-700 ease-out" style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
    </div>
  );
}

export function Ring({ value = 0, size = 64, stroke = 7, children }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#EADEC9" strokeWidth={stroke} fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#1DB99A" strokeWidth={stroke} fill="none" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - value / 100)} style={{ transition: "stroke-dashoffset .8s ease-out" }} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center font-display font-extrabold text-sm">{children ?? `${value}%`}</div>
    </div>
  );
}

export function StatusDot({ status, onClick, testid }) {
  const done = status === "completed";
  const prog = status === "in_progress";
  return (
    <button type="button" onClick={onClick} disabled={!onClick} data-testid={testid}
      className={`h-7 w-7 shrink-0 rounded-full flex items-center justify-center border-2 transition-colors ${done ? "bg-[#1DB99A] border-[#1DB99A] text-white" : prog ? "border-[#F59E0B] bg-amber-50" : "border-[#EADEC9] bg-white"} ${onClick ? "cursor-pointer active:scale-90" : "cursor-default"}`}>
      {done ? <Check className="h-4 w-4" strokeWidth={3} /> : prog ? <span className="h-2.5 w-2.5 rounded-full bg-[#F59E0B]" /> : null}
    </button>
  );
}

export function PageHeader({ eyebrow, title, subtitle, right }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-6 fade-up">
      <div>
        {eyebrow && <div className="sb-eyebrow mb-2">{eyebrow}</div>}
        <h1 className="text-2xl sm:text-3xl font-extrabold text-[#1E2B3C]">{title}</h1>
        {subtitle && <p className="mt-2 text-sm sm:text-base text-[#5C6B73] max-w-2xl">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

export function Loader() {
  return (
    <div className="flex items-center justify-center py-24" data-testid="page-loader">
      <div className="h-8 w-8 rounded-full border-[3px] border-[#EADEC9] border-t-[#1DB99A] animate-spin" />
    </div>
  );
}

const linkRe = /(https?:\/\/[^\s]+)/g;
export function RichText({ text = "", className = "" }) {
  const lines = text.split("\n");
  return (
    <div className={`space-y-2 ${className}`}>
      {lines.map((ln, i) => {
        const l = ln.trim();
        if (!l) return null;
        const bullet = /^[•\-]\s*/.test(l);
        const body = l.replace(/^[•\-]\s*/, "");
        const parts = body.split(linkRe).map((p, j) => linkRe.test(p)
          ? <a key={j} href={p} target="_blank" rel="noreferrer" className="text-[#179B80] underline underline-offset-2 break-all">{p}</a>
          : <span key={j}>{p}</span>);
        const heading = l === l.toUpperCase() && l.length > 3 && /[A-Z]/.test(l) && !bullet;
        if (heading) return <div key={i} className="sb-eyebrow pt-2">{l}</div>;
        return bullet
          ? <div key={i} className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#1DB99A] shrink-0" /><p>{parts}</p></div>
          : <p key={i}>{parts}</p>;
      })}
    </div>
  );
}
